// Werte für andere Prüfpunkte an einer Antwortoption (`cl_options.fill_values`,
// Karte #568, Extern: DGUV-Board #12).
//
// Eine Option eines Auswahl-Prüfpunkts (`select`/`radio`) trägt eine Liste
// `[{ target_id, value }]`. `target_id` ist die Inspect-ID eines Prüfpunkts
// DERSELBEN Aufgabe; wählt der Prüfer die Option in der App, setzt die App die
// Werte dort ein. Vertrag: `features/auswahl-option-geraetevorlage/FEATURE.md`
// im Orchestrator-Repo.
//
// Die Normalisierung macht der Server (`helper/checklist/fill_values.js` in
// 8p2-cld-backend). Diese Datei entscheidet nur, was die Maske anbietet,
// markiert und abschickt.

/**
 * Präfix jeder Wert-Abweisung des Servers zu `fill_values`. Quelle:
 * `FILL_VALUES_MESSAGE_PREFIX` im Backend-Modul. Wird wie
 * `RETENTION_COST_MESSAGE_PREFIX` an der Gestalt der Meldung erkannt.
 */
export const FILL_VALUES_MESSAGE_PREFIX = "Werte für andere Prüfpunkte ";

/** Typen des Auswahl-Prüfpunkts, an dessen Optionen der Abschnitt erscheint. */
export function fillValuesSourceAllowed(fieldtype) {
  return fieldtype === "select" || fieldtype === "radio";
}

/** Typen, die als Ziel erlaubt sind (FEATURE.md, Decision 3). */
export function fillValuesTargetAllowed(fieldtype) {
  return fieldtype === "input" || fieldtype === "textarea";
}

/**
 * Eingabeart des Wertes je Untertyp des Ziels (FEATURE.md, Decision 4). Die
 * App rendert `time` ebenfalls als `datetime-local`.
 */
export function fillValueInputType(subtype) {
  if (subtype === "date") return "date";
  if (subtype === "datetime-local" || subtype === "time") return "datetime-local";
  return "text";
}

const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_FORMAT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * Passt ein Wert zum Speicherformat seiner Eingabeart? Leer passt immer (die
 * Zeile wird dann nur nicht gesendet).
 */
export function fillValueFormatOk(inputType, value) {
  if (value === undefined || value === null || value === "") return true;
  if (inputType === "date") return DATE_FORMAT.test(String(value));
  if (inputType === "datetime-local") return DATETIME_FORMAT.test(String(value));
  return true;
}

/**
 * Trägt die Prüfpunktliste die Felder aus #567? Listen aus einem Endpunkt ohne
 * sie (oder von einem Server vor dem Deploy) haben kein `fieldtype`; dann wird
 * nichts als fehlend markiert.
 */
export function fillValuesListHasTypes(list) {
  if (!Array.isArray(list)) return false;
  return list.some(
    (q) => q && typeof q === "object" && Object.prototype.hasOwnProperty.call(q, "fieldtype")
  );
}

function trimmed(v) {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * Geschwister-Prüfpunkte, die in den Ziel-Wähler gehören: erlaubter Typ, nicht
 * der Auswahl-Prüfpunkt selbst. Ohne Inspect-ID bleiben sie in der Liste, aber
 * `selectable: false`, damit der Anwender sieht, warum er sie nicht wählen kann.
 * Taucht dieselbe Inspect-ID mehrfach auf, ist nur der erste Eintrag wählbar.
 *
 * @returns {Array<{uid, text, inspect_uid, field_subtype, selectable, reason}>}
 *   `reason`: "" | "no_inspect_id" | "duplicate_inspect_id".
 */
export function fillValuesCandidates(list, ownQuestionUid) {
  if (!fillValuesListHasTypes(list)) return [];
  const out = [];
  const seen = {};
  for (const q of list) {
    if (!q || typeof q !== "object") continue;
    if (q.value === ownQuestionUid) continue;
    if (!fillValuesTargetAllowed(q.fieldtype)) continue;
    const inspectUid = trimmed(q.inspect_uid);
    let reason = "";
    if (inspectUid === "") reason = "no_inspect_id";
    else if (seen[inspectUid]) reason = "duplicate_inspect_id";
    if (inspectUid !== "") seen[inspectUid] = true;
    out.push({
      uid: q.value,
      text: q.text,
      inspect_uid: inspectUid,
      field_subtype: q.field_subtype || "",
      selectable: reason === "",
      reason,
    });
  }
  return out;
}

/**
 * Zustand des Ziels einer Zeile:
 *   - "empty"      noch kein Ziel gewählt;
 *   - "unknown"    Liste ohne Typfelder, keine Aussage möglich;
 *   - "ok"         Ziel ist ein wählbarer Kandidat;
 *   - "missing"    kein Geschwister-Prüfpunkt trägt diese Inspect-ID;
 *   - "unsuitable" Ziel existiert, ist aber nicht input/textarea oder der
 *                  Auswahl-Prüfpunkt selbst.
 *
 * @returns {{state: string, subtype: string}}
 */
export function fillValueTargetStatus(list, ownQuestionUid, targetId) {
  const id = trimmed(targetId);
  if (id === "") return { state: "empty", subtype: "" };
  if (!fillValuesListHasTypes(list)) return { state: "unknown", subtype: "" };
  let found = null;
  for (const q of list) {
    if (q && typeof q === "object" && trimmed(q.inspect_uid) === id) {
      found = q;
      break;
    }
  }
  if (!found) return { state: "missing", subtype: "" };
  if (found.value === ownQuestionUid || !fillValuesTargetAllowed(found.fieldtype)) {
    return { state: "unsuitable", subtype: "" };
  }
  return { state: "ok", subtype: found.field_subtype || "" };
}

/**
 * Was gesendet wird: Zeilen mit Ziel und nicht-leerem Wert, je Ziel die erste
 * (gleiche Regel wie der Server). Immer ein Array, auch `[]` zum Löschen.
 */
export function fillValuesPayload(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  const seen = {};
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const target_id = trimmed(r.target_id);
    if (target_id === "") continue;
    const value = r.value === undefined || r.value === null ? "" : String(r.value);
    if (value === "") continue;
    if (seen[target_id]) continue;
    seen[target_id] = true;
    out.push({ target_id, value });
  }
  return out;
}
